import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeProvider';
import { motion } from 'framer-motion';
import i18n from '../i18n';
import { scrollToSection } from '../utils/scrollToSection';
import '../styles/Navbar.css';

const Navbar1: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'el', name: 'Ελληνικά' },
    { code: 'it', name: 'Italiano' },
    { code: 'pl', name: 'Polski' },
    { code: 'fr', name: 'Français' }
  ];

  const handleLanguageChange = (langCode: string) => {
    setCurrentLanguage(langCode);
    i18n.changeLanguage(langCode);
  };

  const handleNavClick = (sectionId: string) => {
    setIsMenuOpen(false);
    setIsSideNavOpen(false);
    scrollToSection(sectionId);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen) {
        const menu = document.querySelector('.navbar-menu');
        if (menu && !menu.contains(e.target as Node)) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    console.log('Menu open:', isMenuOpen);
  }, [isMenuOpen]);

  return (
    <nav className={`navbar ${isDarkMode ? 'dark' : 'light'} ${scrolled ? 'scrolled' : ''}`}>
      {isMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Close menu overlay"
        ></div>
      )}

      <button
        className="side-hamburger"
        onClick={() => setIsSideNavOpen((open) => !open)}
        aria-label="Toggle navigation menu"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect y="6" width="32" height="4" rx="2" fill="currentColor" />
          <rect y="14" width="32" height="4" rx="2" fill="currentColor" />
          <rect y="22" width="32" height="4" rx="2" fill="currentColor" />
        </svg>
      </button>

      {isSideNavOpen && (
        <div
          className="side-nav-overlay"
          onClick={() => setIsSideNavOpen(false)}
          aria-label="Close navigation drawer"
        ></div>
      )}
      <aside className={`side-nav ${isSideNavOpen ? 'open' : ''} ${isDarkMode ? 'dark' : 'light'}`}>
        <button
          className="side-nav-close"
          onClick={() => setIsSideNavOpen(false)}
          aria-label="Close navigation menu"
          style={{
            color: isDarkMode ? '#fff' : '#111',
            background: 'none',
            border: 'none',
            fontSize: '2rem',
            cursor: 'pointer',
            position: 'absolute',
            top: '8px',
            right: '16px',
            zIndex: 2
          }}
        >
          &times;
        </button>
        <button className="side-nav-link" onClick={() => handleNavClick('hero')}>
          {/* Home icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: '8px'}}><path d="M3 12L12 4l9 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 21V13h6v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('nav.home')}
        </button>
        <button className="side-nav-link" onClick={() => handleNavClick('about')}>
          {/* About icon (info) */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: '8px'}}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="8" r="1" fill="currentColor"/></svg>
          {t('nav.about')}
        </button>
        <button className="side-nav-link" onClick={() => handleNavClick('services')}>
          {/* Services icon (cog) */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: '8px'}}><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.09A1.65 1.65 0 0011 3.09V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.09a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.09a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('nav.services')}
        </button>
        <button className="side-nav-link" onClick={() => handleNavClick('gallery')}>
          {/* Gallery icon (image) */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: '8px'}}><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('nav.gallery')}
        </button>
        <button className="side-nav-link" onClick={() => handleNavClick('contact')}>
          {/* Contact icon (mail) */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: '8px'}}><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('nav.contact')}
        </button>
      </aside>

      <div className={`navbar-controls-fixed ${scrolled ? 'scrolled' : ''}`}>
        <div className="language-selector">
          <select
            value={currentLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="language-select"
            aria-label="Select language"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={toggleTheme}
          className="theme-toggle"
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            width: '48px',
            height: '24px',
            borderRadius: '12px',
            padding: '2px',
            position: 'relative',
            backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            minWidth: '48px',
            flexShrink: 0
          }}
        >
          <motion.div
            className="theme-toggle-inner"
            animate={{
              x: isDarkMode ? 24 : 0,
              backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
              boxShadow: isDarkMode 
                ? '0 0 10px rgba(255, 255, 255, 0.2)' 
                : '0 0 10px rgba(0, 0, 0, 0.1)',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
            transition={{ 
              type: "spring", 
              stiffness: 500, 
              damping: 30,
              duration: 0.3
            }}
          >
            {isDarkMode ? (
              <motion.svg 
                className="theme-icon" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                initial={{ rotate: -45, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                style={{ 
                  width: '14px', 
                  height: '14px',
                  display: 'block',
                  marginLeft: '1px',
                  marginTop: '1px',
                }}
              >
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
              </motion.svg>
            ) : (
              <motion.svg 
                className="theme-icon" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                initial={{ rotate: 45, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                style={{ 
                  width: '14px', 
                  height: '14px',
                  display: 'block',
                  marginLeft: '1px',
                  marginTop: '1px',
                }}
              >
                <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
              </motion.svg>
            )}
          </motion.div>
        </button>
        <button 
          className="navbar-logo"
          onClick = {() => handleNavClick('hero')}
        >
          Anchor Management
        </button>
      </div>
    </nav>
  );
};

export default Navbar1; 